import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Plus } from "lucide-react";
import { CopyProductLinkButton } from "./CopyProductLinkButton";
import { flyToCart } from "../lib/flyToCart";
import { IMAGES, toAvif } from "../lib/products";
import { useCart } from "../context/CartContext";
import { useBulkPromos, formatRuleSavings } from "../lib/bulkPromos";
import DosageLabel from "./DosageLabel";
import { CoaBadge } from "./CoaBadge";
import SafeImage from "./SafeImage";

function stockLabel(stock, comingSoon) {
  if (comingSoon)
    return { text: "COMING SOON", color: "#ff3b30", disabled: true };
  if (stock === null || stock === undefined) return null;
  const n = Number(stock);
  if (n <= 0) return { text: "Out of stock", color: "#ff6b6b", disabled: true };
  if (n <= 10) return { text: `Only ${n} left`, color: "#ff6b6b", pulse: true };
  return { text: "In stock", color: "rgba(255,255,255,0.6)" };
}

export default function ProductCard({ product, index = 0, fromCategory }) {
  const { addItem } = useCart();
  const linkState = fromCategory ? { fromCategory } : undefined;
  const comingSoon = !!product.coming_soon;
  const stock = stockLabel(product.stock, comingSoon);
  const soldOut = stock?.disabled;
  // Live bulk-promo teaser — when at least one auto-apply rule exists
  // we cycle through every rule under the price at 1s intervals so the
  // customer sees the full ladder (3+ → 5+ → 10+ → …) instead of just
  // the lowest threshold. Pure advertising; the actual discount fires
  // on Detail/Cart once qty crosses the threshold.
  const bulkRules = useBulkPromos();
  const [teaserIdx, setTeaserIdx] = useState(0);
  useEffect(() => {
    if (!bulkRules || bulkRules.length <= 1) return undefined;
    // Reset to 0 whenever the rule set changes so we don't flash an
    // out-of-bounds index briefly after the polling fetch updates it.
    setTeaserIdx(0);
    const id = setInterval(() => {
      setTeaserIdx((i) => (i + 1) % bulkRules.length);
    }, 3000);
    return () => clearInterval(id);
  }, [bulkRules]);
  const teaserRule =
    bulkRules && bulkRules.length
      ? bulkRules[teaserIdx % bulkRules.length]
      : null;
  const justLaunched =
    !product.coming_soon &&
    product.launched_at &&
    Date.now() - new Date(product.launched_at).getTime() <
      7 * 24 * 60 * 60 * 1000;

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="group relative flex flex-col bg-[#0f1418] text-white rounded-md overflow-hidden border border-white/10 hover:-translate-y-1 hover:border-[#20a6da]/50 hover:shadow-[0_12px_40px_-8px_rgba(32,166,218,0.35)] transition-all duration-300 w-full max-w-[2.8in] mx-auto sm:max-w-none"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className="relative">
        <Link
          to={`/shop/${product.id}`}
          state={linkState}
          data-testid={`details-${product.id}`}
          aria-label={product.name}
          className="block relative aspect-square overflow-hidden bg-[#0a0a0a]"
        >
          <SafeImage
            src={toAvif(product.image_url || IMAGES[product.id])}
            alt={`${product.name}${product.dosage && !product.name.includes(product.dosage) ? ` ${product.dosage}` : ""} research peptide vial`}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          />
          <DosageLabel product={product} />
          {justLaunched && (
            <span
              data-testid={`just-launched-${product.id}`}
              className="absolute bottom-3 right-3 rotate-2 rounded-sm bg-[#7CE3A6] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#0a0a0a] shadow-[0_4px_14px_rgba(124,227,166,0.45)]"
            >
              Just launched!
            </span>
          )}
          {product.bundle_items?.length ? (
            <span
              data-testid={`bundle-badge-${product.id}`}
              className="absolute top-3 right-3 rounded-sm bg-[#20a6da] text-[#0a0a0a] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em]"
            >
              Bundle
            </span>
          ) : null}
      </Link>
        <CopyProductLinkButton productId={product.id} productName={product.name} />
        <CoaBadge
          product={product}
          className={product.bundle_items?.length ? "!top-12 !right-3" : "!top-3 !right-3"}
        />
      </div>

      <div className="p-6 pr-16 flex flex-col gap-2 flex-1 relative text-white">
        <h3 className="font-display text-lg font-[700] uppercase tracking-tight leading-tight">
          <Link
            to={`/shop/${product.id}`}
            state={linkState}
            className="hover:text-[#20a6da] transition-colors"
          >
            {product.name}
          </Link>
        </h3>

        {product.bundle_items?.length ? (
          <p className="text-[11px] text-[#bfcde3]/70 uppercase tracking-widest">
            Includes:{" "}
            {product.bundle_items
              .map((b) => `${b.quantity}× ${b.product_id.replace("reta-", "")}`)
              .join(", ")}
          </p>
        ) : null}

        {stock && (
          <p
            data-testid={`stock-${product.id}`}
            className={`overline text-[10px] ${stock.pulse ? "low-stock-pulse" : ""}`}
            style={{ color: stock.color }}
          >
            {stock.text}
          </p>
        )}

        {!comingSoon && (
        <p className="mt-2 font-display tracking-tight tabular-nums text-white">
          {(() => {
            const base = Number(product.price);
            const sale =
              product.sale_price !== null &&
              product.sale_price !== undefined &&
              Number(product.sale_price) > 0 &&
              Number(product.sale_price) < base
                ? Number(product.sale_price)
                : null;
            if (sale === null) {
              return (
                <span
                  className="text-2xl font-[700]"
                  data-testid={`price-${product.id}`}
                >
                  ${base.toFixed(2)}
                </span>
              );
            }
            return (
              <span className="flex items-baseline gap-2 flex-wrap">
                <span
                  className="text-base font-[600] text-white/55 line-through decoration-[2px] decoration-double"
                  data-testid={`original-price-${product.id}`}
                >
                  ${base.toFixed(2)}
                </span>{" "}
                <span
                  className="text-3xl font-[800] text-[#ff6b6b]"
                  data-testid={`sale-price-${product.id}`}
                >
                  Sale ${sale.toFixed(2)}
                </span>
              </span>
            );
          })()}
        </p>
        )}

        {teaserRule && !soldOut && !comingSoon ? (
          <p
            key={teaserRule.min_group_qty}
            data-testid={`bulk-teaser-${product.id}`}
            className="text-[11px] font-semibold uppercase tracking-widest text-[#7CE3A6] animate-promo-cycle"
            title="Auto-applied at checkout when this product's quantity hits the threshold."
          >
            Buy {teaserRule.min_group_qty}+ — {formatRuleSavings(teaserRule)}
          </p>
        ) : null}

        {!comingSoon && (
        <button
          type="button"
          data-testid={`add-to-cart-${product.id}`}
          onClick={(e) => {
            flyToCart(e.currentTarget.closest("article")?.querySelector("img"));
            addItem(product, 1);
          }}
          disabled={soldOut}
          aria-label={soldOut ? "Out of stock" : `Add ${product.name} to cart`}
          className="absolute bottom-6 right-4 h-8 px-2 sm:right-6 sm:h-10 sm:px-3 inline-flex items-center justify-center gap-0.5 rounded-md border border-white bg-[#1A1A1A] text-[#F9F9F7] hover:bg-[#333] hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0"
        >
          <Plus className="h-2.5 w-2.5 sm:h-[11px] sm:w-[11px]" strokeWidth={2.75} />
          <ShoppingCart className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={2.25} />
        </button>
        )}
      </div>
    </article>
  );
}
